/**
 * Restaurar una copia de seguridad.
 *
 * ---------------------------------------------------------------------------
 *  Lo que se hace antes de tocar nada
 * ---------------------------------------------------------------------------
 *
 * Cuando el usuario elige un archivo, lo primero es **leerlo y enseñarle qué trae**
 * —cuántos movimientos, cuántas cuentas, de cuándo es— y solo después preguntar qué
 * hacer con lo que ya hay.
 *
 * Ese paso no es adorno. Restaurar puede borrar meses de trabajo, y quien lo hace
 * tiene derecho a ver antes qué va a entrar. Un botón que dice «restaurar» y actúa sin
 * más es una trampa.
 *
 * Y el archivo se lee **en el navegador**, sin subirlo, para poder decir si es válido
 * antes de mandar nada. Si no lo es, no hay ni una petición.
 */

import { AlertTriangle, FileUp, RotateCcw } from 'lucide-react';
import { useRef, useState } from 'react';

import { restoreBackup, type RestoreResultDto } from '../lib/endpoints';
import { ApiError } from '../lib/api';
import { Note } from './ui';

/** Lo que se enseña del archivo antes de aplicarlo. */
interface Preview {
  /** El archivo entero, tal cual. Es lo que se mandará. */
  raw: Record<string, unknown>;
  exportedAt: string | null;
  counts: Array<{ label: string; count: number }>;
  total: number;
}

/** Los nombres en castellano de lo que trae el archivo. */
const LABELS: Record<string, string> = {
  accounts: 'cuentas',
  transactions: 'movimientos',
  categories: 'categorías propias',
  tags: 'etiquetas',
  budgets: 'presupuestos',
  debts: 'deudas',
  debtPayments: 'pagos de deuda',
  fiadoStores: 'tiendas de fiado',
  fiadoEntries: 'apuntes de fiado',
  recurringRules: 'gastos fijos',
  splits: 'gastos compartidos',
  goals: 'metas',
  goalContributions: 'aportes a metas',
  receipts: 'recibos',
};

export function RestoreBackup(): React.JSX.Element {
  const fileInput = useRef<HTMLInputElement>(null);
  const [preview, setPreview] = useState<Preview | null>(null);
  const [problem, setProblem] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<RestoreResultDto | null>(null);

  /** Lee el archivo elegido, sin mandarlo a ningún sitio. */
  const readFile = async (file: File): Promise<void> => {
    setPreview(null);
    setResult(null);
    setProblem(null);

    try {
      const text = await file.text();
      const parsed: unknown = JSON.parse(text);

      if (typeof parsed !== 'object' || parsed === null) {
        setProblem('Ese archivo no tiene el formato de un respaldo.');
        return;
      }

      const root = parsed as Record<string, unknown>;

      if (root['app'] !== 'ElDescuadre') {
        setProblem(
          'Ese archivo no lo generó ElDescuadre. Comprueba que elegiste el correcto.',
        );
        return;
      }

      const data = root['data'];
      if (typeof data !== 'object' || data === null) {
        setProblem('El respaldo no trae datos.');
        return;
      }

      const counts = Object.entries(data as Record<string, unknown>)
        .filter(([, value]) => Array.isArray(value) && value.length > 0)
        .map(([key, value]) => ({
          label: LABELS[key] ?? key,
          count: (value as unknown[]).length,
        }))
        .sort((a, b) => b.count - a.count);

      setPreview({
        raw: root,
        exportedAt: typeof root['exportedAt'] === 'string' ? root['exportedAt'] : null,
        counts,
        total: counts.reduce((acc, item) => acc + item.count, 0),
      });
    } catch {
      setProblem('No se pudo leer el archivo. ¿Está completo y sin daños?');
    }
  };

  const apply = async (mode: 'replace' | 'merge'): Promise<void> => {
    if (!preview) return;

    if (
      mode === 'replace' &&
      !window.confirm(
        'Se va a BORRAR todo lo que tienes ahora y se pondrá lo del archivo. Esto no se puede deshacer. ¿Continuar?',
      )
    ) {
      return;
    }

    setBusy(true);
    setProblem(null);

    try {
      const outcome = await restoreBackup(preview.raw, mode);
      setResult(outcome);
      setPreview(null);
      if (fileInput.current) fileInput.current.value = '';
    } catch (error) {
      setProblem(
        error instanceof ApiError ? error.message : 'No se pudo restaurar el respaldo.',
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-3">
      {!preview && (
        <>
          <label className="flex cursor-pointer items-center gap-3 rounded-ed border border-dashed border-ed-bg-border/50 px-4 py-3 text-sm transition-colors hover:border-ed-gold/50">
            <FileUp size={18} className="shrink-0 text-ed-text-muted" aria-hidden="true" />
            <span>
              <span className="block font-medium">Restaurar desde un archivo</span>
              <span className="block text-xs text-ed-text-muted">
                Elige el respaldo que descargaste antes.
              </span>
            </span>
            <input
              ref={fileInput}
              type="file"
              accept="application/json,.json"
              className="sr-only"
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file) void readFile(file);
              }}
            />
          </label>

          <p className="text-xs text-ed-text-muted">
            El archivo se lee <strong>en tu teléfono</strong> para comprobar que sirve. No se
            envía nada hasta que confirmes.
          </p>
        </>
      )}

      {/* Lo que trae el archivo, antes de decidir nada. */}
      {preview && (
        <div className="rounded-ed border border-ed-cyan/40 bg-ed-cyan/5 p-3">
          <p className="text-sm font-medium text-ed-cyan-light">
            El archivo trae {preview.total.toLocaleString('es-CO')}{' '}
            {preview.total === 1 ? 'registro' : 'registros'}
          </p>

          {preview.exportedAt && (
            <p className="mt-0.5 text-xs text-ed-text-muted">
              Se hizo el {new Date(preview.exportedAt).toLocaleString('es-CO')}.
            </p>
          )}

          <ul className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-xs text-ed-text-silver">
            {preview.counts.map((item) => (
              <li key={item.label}>
                <strong className="ed-money">{item.count.toLocaleString('es-CO')}</strong>{' '}
                {item.label}
              </li>
            ))}
          </ul>

          <p className="mt-3 border-t border-ed-cyan/20 pt-2 text-xs font-medium">
            ¿Qué hago con lo que ya tienes?
          </p>

          <div className="mt-2 grid gap-2 sm:grid-cols-2">
            <button
              type="button"
              className="ed-btn-secondary !min-h-[44px] text-xs"
              disabled={busy}
              onClick={() => void apply('merge')}
            >
              <FileUp size={15} aria-hidden="true" />
              Añadirlo a lo que hay
            </button>

            <button
              type="button"
              className="ed-btn-danger !min-h-[44px] text-xs"
              disabled={busy}
              onClick={() => void apply('replace')}
            >
              <RotateCcw size={15} aria-hidden="true" />
              Reemplazar todo
            </button>
          </div>

          <button
            type="button"
            className="ed-btn-ghost mt-2 w-full text-xs"
            disabled={busy}
            onClick={() => {
              setPreview(null);
              if (fileInput.current) fileInput.current.value = '';
            }}
          >
            Cancelar
          </button>

          <p className="mt-2 flex items-start gap-1.5 text-[11px] text-ed-text-muted">
            <AlertTriangle size={12} className="mt-0.5 shrink-0" aria-hidden="true" />
            <span>
              <strong>Añadir</strong> conserva lo que tienes y le suma lo del archivo. Si el
              archivo es el mismo que ya está, no duplica nada.{' '}
              <strong>Reemplazar</strong> borra todo lo actual y pone lo del archivo.
            </span>
          </p>
        </div>
      )}

      {busy && <p className="text-sm text-ed-text-muted">Restaurando…</p>}

      {problem && (
        <p
          role="alert"
          className="rounded-ed border border-ed-coral-deep/40 bg-ed-coral-deep/10 px-3 py-2 text-sm text-ed-coral-light"
        >
          {problem}
        </p>
      )}

      {result && (
        <p
          role="status"
          className="rounded-ed border border-ed-success/40 bg-ed-success/10 px-3 py-2 text-sm text-ed-success"
        >
          Listo.{' '}
          {Object.entries(result.restored)
            .map(([key, count]) => `${count.toLocaleString('es-CO')} ${LABELS[key] ?? key}`)
            .join(', ') || 'No había nada que restaurar.'}
          {result.skipped > 0 && (
            <>
              {' '}
              Se descartaron {result.skipped.toLocaleString('es-CO')}{' '}
              {result.skipped === 1 ? 'registro' : 'registros'} que venían incompletos.
            </>
          )}
          {result.alreadyPresent > 0 && (
            <>
              {' '}
              Las {result.alreadyPresent} categorías del sistema ya estaban y no se tocaron.
            </>
          )}
        </p>
      )}

      <Note>
        Un respaldo sin forma de restaurarlo no es un respaldo. Aquí está el camino de
        vuelta, y <strong>se lee el archivo entero antes de tocar nada</strong>: si algo
        no encaja, no se cambia ni un dato.
      </Note>
    </div>
  );
}
